import * as React from 'react';

import { useMenuNavigation } from 'containers/Tiptap/hooks/use-menu-navigation';
import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';

import { BanIcon } from 'containers/Tiptap/components/tiptap-icons/ban-icon';
import { TextColorSmallIcon } from 'containers/Tiptap/components/tiptap-icons/text-color-small-icon';

import { Button } from 'containers/Tiptap/components/tiptap-ui-primitive/button';
import { Popover, PopoverTrigger, PopoverContent } from 'containers/Tiptap/components/tiptap-ui-primitive/popover';
import { Separator } from 'containers/Tiptap/components/tiptap-ui-primitive/separator/separator';

import { ColorTextButton, TEXT_COLORS, canColorText } from 'containers/Tiptap/components/tiptap-ui/color-text-button';

import 'containers/Tiptap/components/tiptap-ui/color-highlight-button/color-highlight-button.scss';
import 'containers/Tiptap/components/tiptap-ui/color-text-popover/color-text-popover.scss';

import { StyledDefaultTextSwatch, StyledSwatchRow, StyledTextColourIcon, StyledTextColourRing } from './styles';

const REMOVE_ITEM = { label: 'Remove text colour', value: 'none' };

const ColorTextToolbarContent: React.FC<{ onClose: () => void }> = ({ onClose }) => {
  const { editor } = useTiptapEditor();
  const containerRef = React.useRef<HTMLDivElement>(null);

  const removeColor = React.useCallback(() => {
    editor?.chain().focus().unsetColor().run();
    onClose();
  }, [editor, onClose]);

  const menuItems = React.useMemo(() => [...TEXT_COLORS, REMOVE_ITEM], []);

  const { selectedIndex } = useMenuNavigation({
    containerRef,
    items: menuItems,
    orientation: 'both',
    onSelect: (item) => {
      if (item.value === REMOVE_ITEM.value) {
        removeColor();
        return;
      }
      editor?.chain().focus().toggleMark('textStyle', { color: item.value }).run();
      onClose();
    },
    onClose,
    autoSelectFirstItem: false,
  });

  return (
    <div ref={containerRef} className="tiptap-color-highlight-content" tabIndex={0} role="menu">
      <StyledSwatchRow className="tiptap-button-group" data-orientation="horizontal">
        {TEXT_COLORS.map((color, index) => (
          <ColorTextButton
            key={color.value}
            textColor={color.value}
            label={color.label}
            aria-label={color.label}
            tooltip={color.label}
            tabIndex={index === selectedIndex ? 0 : -1}
            data-highlighted={selectedIndex === index}
            onClick={onClose}
          >
            {index === 0 ? (
              <StyledDefaultTextSwatch className="tiptap-button-color-text">
                <TextColorSmallIcon className="tiptap-button-icon" />
              </StyledDefaultTextSwatch>
            ) : undefined}
          </ColorTextButton>
        ))}
      </StyledSwatchRow>
      <Separator />
      <div className="tiptap-button-group">
        <Button
          onClick={removeColor}
          aria-label={REMOVE_ITEM.label}
          tooltip={REMOVE_ITEM.label}
          tabIndex={selectedIndex === TEXT_COLORS.length ? 0 : -1}
          type="button"
          role="menuitem"
          data-style="ghost"
          data-highlighted={selectedIndex === TEXT_COLORS.length}
        >
          <BanIcon className="tiptap-button-icon" />
        </Button>
      </div>
    </div>
  );
};

export const ColorTextToolbarPopover: React.FC = () => {
  const { editor } = useTiptapEditor();
  const [isOpen, setIsOpen] = React.useState(false);
  const [, forceRender] = React.useReducer((count: number) => count + 1, 0);

  React.useEffect(() => {
    if (!editor) return;
    editor.on('transaction', forceRender);
    return () => {
      editor.off('transaction', forceRender);
    };
  }, [editor]);

  if (!editor || !editor.isEditable) {
    return null;
  }

  const isDisabled = !canColorText(editor);
  const activeColour = editor.getAttributes('textStyle').color as string | undefined;

  return (
    <Popover open={isOpen} onOpenChange={setIsOpen}>
      <PopoverTrigger asChild>
        <Button
          type="button"
          data-style="ghost"
          data-disabled={isDisabled}
          disabled={isDisabled}
          role="button"
          tabIndex={-1}
          aria-label="Text colour"
          tooltip="Text colour"
        >
          <StyledTextColourRing className="tiptap-button-color-text-popover">
            <StyledTextColourIcon className="tiptap-button-icon" $colour={activeColour} />
          </StyledTextColourRing>
        </Button>
      </PopoverTrigger>

      <PopoverContent side="top" align="center" sideOffset={14} aria-label="Text colours">
        <ColorTextToolbarContent onClose={() => setIsOpen(false)} />
      </PopoverContent>
    </Popover>
  );
};

export default ColorTextToolbarPopover;
